// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  useDisclosure,
  useToast,
  Input,
  Button,
  Flex,
  IconButton
} from '@chakra-ui/react'
import { EditIcon, AddIcon, DeleteIcon, SmallCloseIcon } from '@chakra-ui/icons'
import DataTable from 'react-data-table-component'
import { formElementInterface, keyValue, keyValueObject, keyValuePair } from '../lib/types'
import { FormComponent } from './formComponent'
import { generateFormJSONForDataTables, JSONToTable } from './utils'
import { getMatchFromGeneralDictionary, graphQLSendDataMappings, masterTypesInnerDataForTableRendering } from '../lib/formElementMappings'
import { ApolloError, useMutation } from '@apollo/client'
import { getQueryFor } from '../queries/queryMappints'

import { formatVariables } from './dataTable.support'
import { SystemForm } from '../pages/systemForm'
import { ConfirmDialog } from './confirmDialog'
import { ExpandableRowsComponent } from 'react-data-table-component/dist/DataTable/types'

interface SNPDataTableFunctionType {
  title: string
  data: keyValue[] | undefined
  allTypes: keyValueObject | undefined
}
interface DataRow {
  name: string
  sortable: boolean
}
export const customStyles = {
  headCells: {
    style: {
      paddingLeft: '8px', // override the cell padding for head cells
      paddingRight: '8px',
      fontWeight: 'bold',
      fontSize: '12px',
      paddingTop: '4px',
      paddingBottom: '4px',
      textTransform: 'capitalize',
      borderRight: '1px solid #f4f4f4',
      backgroundColor: '#fafdff',
      minWidth: '100px !important'
    }
  },
  cells: {
    style: {
      paddingLeft: '8px', // override the cell padding for data cells
      paddingRight: '8px',
      fontSize: '12px'
    }
  }
}
const convertArrayOfObjectsToCSV = (array: keyValue[]) => {
  const columnDelimiter = ','
  const lineDelimiter = '\n'
  const keys = Object.keys(array[0])
  const result = [
    keys.join(columnDelimiter),
    ...array.map(item => keys.map(key => masterTypesInnerDataForTableRendering(key, item[key] as string | keyValue)).join(columnDelimiter))
  ].join(lineDelimiter)
  return result
}
function downloadCSV(array: keyValue[]): void {
  const link = document.createElement('a')
  let csv = convertArrayOfObjectsToCSV(array)
  if (csv == null) return

  const filename = 'export.csv'

  if (!csv.match(/^data:text\/csv/i)) {
    csv = `data:text/csv;charset=utf-8,${csv}`
  }

  link.setAttribute('href', encodeURI(csv))
  link.setAttribute('download', filename)
  link.click()
}
const Export = ({ onExport }: { onExport: (value: string) => void }) => (
  <Button onClick={e => onExport((e.target as HTMLButtonElement).value)} fontSize="12px">
    Export CSV
  </Button>
)

interface FilterComponentProps {
  onFilter: (e: React.ChangeEvent<HTMLInputElement>) => void
  onClear: () => void
  filterText: string
}

const FilterComponent: React.FC<FilterComponentProps> = ({ onFilter, onClear, filterText }) => (
  <Flex gap="4px" maxW="300px">
    <Input type="text" fontSize="12px" placeholder="Filter..." value={filterText} onChange={onFilter} />
    <IconButton size="md" onClick={onClear} color="current" aria-label={`Clear Filter`} title="Clear Filter">
      <SmallCloseIcon />
    </IconButton>
  </Flex>
)

export const SNPDataTable = ({ title, data, allTypes }: SNPDataTableFunctionType): JSX.Element => {
  const [formInputData, setFormInputData] = useState<formElementInterface[] | undefined>()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [currentId, setCurrentId] = useState<string>('')
  const [systemFormMode, setSystemFormMode] = useState<'edit' | 'create'>('create')
  const [systemComponentRawData, setSystemComponentRawData] = useState<keyValueObject>()
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [IDToDelete, setIDToDelete] = useState<string | null>(null)
  const [filterText, setFilterText] = useState('')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [renderForm] = useState<number>(1)
  const [formKey, setFormKey] = useState<number>(0)
  const toast = useToast()
  useEffect(() => {
    return () => {
      setIDToDelete(null)
    }
  }, [data])
  const newForm = (): void => {
    setSystemFormMode('create')
    const tableData = data ?? []
    const [firstRow] = tableData
    if (allTypes) {
      const formJSON = generateFormJSONForDataTables(firstRow ?? [], allTypes, queries?.dataKey ?? null)
      const emptyValuedFormJSON = formJSON.reduce((reducer: formElementInterface[], item) => [...reducer, { ...item, value: '' }], [])
      setFormInputData(emptyValuedFormJSON)
    }
    setFormKey(k => k + 1)
    if (allTypes) {
      onOpen()
    }
  }
  const [first] = data ?? []
  const queries = getQueryFor(first?.__typename as string)
  const actionsMemo = useMemo(
    () => (
      <Box display="flex" gap="24px" justifyContent={'space-between'}>
        <Box>
          <FilterComponent onFilter={e => setFilterText(e.target.value)} onClear={() => setFilterText('')} filterText={filterText} />
        </Box>
        <Box display="flex" gap="8px" alignItems="center">
          <Export onExport={() => downloadCSV(data ?? [])} />
          <Button title="create new entry" colorScheme={'blue'} color="white" p="2" height={'36px'} onClick={newForm}>
            <AddIcon w={3} />
          </Button>
        </Box>
      </Box>
    ),
    [data, filterText, title]
  )
  const showErrorMessage = useCallback((error: ApolloError): string => {
    if (error.clientErrors?.length) {
      return `ERR01: ${error.clientErrors[0].message}`
    }
    if (error.graphQLErrors?.length) {
      return `ERR02: ${error.graphQLErrors[0].message}`
    }
    if (error.networkError && !('result' in error.networkError)) {
      return `ERR03 :${error.networkError.message}`
    }
    if (error.networkError && 'result' in error.networkError) {
      return `ERR03 :${(error.networkError.result as { errors: { message: string }[] })?.errors?.[0].message}`
    }
    return error.message ?? 'Something went wrong'
  }, [])

  const [updateElement] =
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useMutation(queries.update, {
      variables: {},
      refetchQueries: [{ query: queries.refetch }],
      onCompleted: () => {
        toast({
          title: 'Success',
          description: `${title} saved`,
          status: 'success',
          duration: 4000,
          isClosable: true
        })
      },
      onError: (error: ApolloError) => {
        toast({
          title: 'Error',
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          description: showErrorMessage(error),
          status: 'error',
          duration: 4000,
          isClosable: true
        })
      }
    })
  const [deleteElement] = useMutation(queries?.delete, {
    variables: {},
    refetchQueries: [{ query: queries.refetch }],
    onCompleted: () => {
      toast({
        title: 'Success',
        description: 'Removed',
        status: 'success',
        duration: 4000,
        isClosable: true
      })
    },
    onError: (error: ApolloError) => {
      toast({
        title: 'Error',
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        description: showErrorMessage(error),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    }
  })

  const [mutateFunction] = useMutation(queries.create, {
    variables: {},
    errorPolicy: 'none',
    refetchQueries: [{ query: queries.refetch }],
    onCompleted: data => {
      toast({
        title: 'Success',
        description: 'Saved',
        status: 'success',
        duration: 4000,
        isClosable: true
      })
    },
    onError: (error: ApolloError) => {
      toast({
        title: 'Error',
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        description: showErrorMessage(error),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    }
  })
  const performEdit = (id: string, rowData: keyValue, allTypes?: keyValueObject): void => {
    if (!allTypes) {
      return
    }
    setSystemFormMode('edit')

    // generateFormJSONForDataTables is where the form element mapping happens.
    setCurrentId(id)
    setFormInputData(generateFormJSONForDataTables(rowData, allTypes, queries?.dataKey ?? null))
    setFormKey(k => k + 1)
    if (!isOpen) {
      onOpen()
    }
  }
  const performDelete = (row: keyValue): void => {
    setIDToDelete((row?.id as string) ?? null)
    setShowConfirm(true)
  }
  const generateColumns = (
    data: keyValue
  ): {
    columns: DataRow[]
    expandableRowsComponent: ExpandableRowsComponent<keyValue> | undefined
  } => {
    const columns = Object.keys(data ?? {})?.map(key => {
      return {
        name: key !== 'id' ? (getMatchFromGeneralDictionary(key) as string) : '',
        selector: (row: keyValue) => {
          const returnValue = masterTypesInnerDataForTableRendering(key, row[key] as string | keyValue)

          if (key === 'id') {
            return (
              <>
                <Button
                  onClick={() => {
                    setSystemComponentRawData(row as keyValueObject)
                    performEdit(row[key] as string, row, allTypes)
                  }}
                >
                  <EditIcon w={3} />
                </Button>

                <Button
                  onClick={() => {
                    performDelete(row)
                  }}
                  ml="3"
                >
                  <DeleteIcon w={3} />
                </Button>
              </>
            )
          }
          if (typeof returnValue === 'string' || typeof returnValue === 'boolean' || typeof returnValue === 'number') {
            return returnValue
          } else {
            return '-'
          }
        },
        sortable: true,
        reorder: true,
        wrap: true,
        grow: 10,
        allowOverflow: true,
        omit: key === '__typename'
      }
    })

    return {
      columns: columns ?? [],
      expandableRowsComponent: title === 'Systems' ? dataRow => <JSONToTable rawData={dataRow?.data as keyValueObject} title={title} /> : undefined
    }
  }

  const performUpdate = (variables: keyValueObject): void => {
    if (updateElement) {
      updateElement(variables)
        .then(res => {})
        .catch(error => {})
    }
  }
  const formSubmit = (formData: keyValue, selectBoxLocalValues: keyValueObject, groupElementLocalValues: keyValueObject): void => {
    const nestedFormObject = Object.keys(formData).reduce((reducer, item) => {
      const getMasterDataTypes = (): keyValuePair | keyValueObject | keyValue[] | string | number | boolean => {
        return graphQLSendDataMappings(item, formData[item] as string, queries?.dataKey ?? null)
      }

      return {
        ...reducer,
        [item]: getMasterDataTypes()
      }
    }, {})

    const elementInputVariable = {
      ...nestedFormObject
    }

    if (systemFormMode === 'edit') {
      if (queries) {
        performUpdate({
          variables: formatVariables(queries.dataKey, {
            ...elementInputVariable,
            id: currentId
          })
        })
      }
    } else {
      if (mutateFunction && queries) {
        mutateFunction({
          variables: formatVariables(queries.dataKey, {
            ...elementInputVariable
          })
        })
          .then(res => {})
          .catch(err => {})
      }
    }
  }
  const handleConfirm = (): void => {
    setShowConfirm(false)

    if (deleteElement) {
      deleteElement({ variables: { id: IDToDelete } })
        .then(res => {
          setIDToDelete(null)
        })
        .catch(error => {})
    }
  }

  const [filteredData, setFilteredData] = useState<keyValue[] | undefined>(data)
  useEffect(() => {
    setFilteredData(
      data?.filter(row => {
        return Object.keys(row).some(key => {
          return String(JSON.stringify(row[key])).toLowerCase().includes(filterText.toLowerCase())
        })
      })
    )
  }, [filterText, data])

  return (
    <>
      {showConfirm && (
        <ConfirmDialog
          callBack={handleConfirm}
          cancelCallBack={() => {
            setShowConfirm(false)
          }}
        />
      )}

      <Box p="6" pt="0" overflow={'auto'}>
        {data && data.length > 0 && (
          <DataTable
            {...generateColumns(data[0])}
            data={filteredData ?? []}
            pagination
            customStyles={customStyles}
            keyField="id"
            fixedHeader
            responsive
            highlightOnHover
            persistTableHead
            striped
            paginationPerPage={10}
            actions={actionsMemo}
            {...(title === 'Systems' && { expandableRows: true })}
            paginationComponentOptions={{ selectAllRowsItem: true }}
          />
        )}
        {(data?.length === 0 || !data) && <Heading>No Data</Heading>}
      </Box>

      <Drawer isOpen={isOpen} onClose={onClose} size={title === 'Systems' ? 'md' : 'md'} closeOnOverlayClick={false}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize={'23px'} color={'purple.700'} fontWeight="black" shadow="md" border="1">
            {systemFormMode === 'create' ? 'Create' : 'Update'} Form
          </DrawerHeader>

          <DrawerBody color={'000'} p="5">
            {formInputData && title !== 'Systems' && (
              <FormComponent
                key={formKey}
                cssProps={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'stretch',
                  bg: 'red.100'
                }}
                onSubmitCallBack={(formData: keyValue, selectBoxLocalValues: keyValueObject, groupElementLocalValues: keyValueObject) => {
                  formSubmit(formData, selectBoxLocalValues, groupElementLocalValues)
                }}
                inputData={formInputData}
                renderForm={renderForm}
              />
            )}

            {title === 'Systems' && allTypes && (
              <SystemForm
                mode={systemFormMode}
                formData={systemComponentRawData ?? {}}
                allTypes={allTypes}
                callBack={() => {
                  setTimeout(() => {
                    onClose()
                  }, 1000)
                }}
              />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  )
}
